import { FC, useEffect, useMemo, useState } from 'react';
import { useContextSelector as useCS } from 'use-context-selector';
import { FormContainer, useFormContext } from 'react-hook-form-mui';
import { AccountSettingsGoogleIcon } from 'assets';
import {
  ProfileConfigContext,
  TProfileConfigContextState,
} from 'domain/profileConfig/ProfileConfigContext';
import { GlobalConfigContext } from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { profileConfigSelectors } from 'domain/profileConfig/selectors';
import { TUpdate } from 'domain/types';
import { Button, Input, Spinner } from 'view/components';
import { EditingHeader } from 'view/pages/profile/components';
import {
  commonEmailValidationRules,
  getCommonPasswordValidationRules,
  parseError,
} from 'view/pages/auth/helpers';
import { useUpdatePassword } from 'view/pages/profile/hooks/useUpdatePassword';
import { useUserLogOut } from 'view/pages/auth/hooks/useUserLogOut';
import { useUpdateEmail } from 'view/pages/profile/hooks/useUpdateEmail';
import * as CS from 'view/pages/profile/commonStyled';
import { RolesEnum } from 'infrastructure/enums/RolesEnum';
import { Tooltip } from '@mui/material';
import { showSystemToast } from 'view/components/SystemToast';
import * as S from './styled';

type AccountSettingsMainContentProps = {
  email: string;
  provider: string;
  showProfile: boolean;
  jobSeekerProfile: any;
  roleName: any;
  setIsEditingEmail: (value: boolean) => void;
  setIsEditingPassword: (value: boolean) => void;
};

const AccountSettingsMainContent: FC<AccountSettingsMainContentProps> = ({
  email,
  provider,
  showProfile,
  jobSeekerProfile,
  roleName,
  setIsEditingEmail,
  setIsEditingPassword,
}) => {
  const isDisabled = provider !== 'email';
  const isShowHideDisabled =
    roleName === RolesEnum.JOB_SEEKER &&
    jobSeekerProfile &&
    !jobSeekerProfile?.hasWorkPreferencesFilled;
  const update: TUpdate<TProfileConfigContextState> | undefined = useCS(
    ProfileConfigContext,
    profileConfigSelectors.update
  );

  const onHandleClickHideButton = () => {
    if (update) update({ isOpenHideAccountModal: true });
  };

  const onHandleClickShowButton = () => {
    if (update) update({ isOpenShowAccountModal: true });
  };

  const onHandleClickDeleteButton = () => {
    if (update) update({ isOpenDeleteAccountModal: true });
  };

  return (
    <>
      <S.EditContainer marginBottom="16px">
        <S.EditContainerBlock>
          <S.EditContainerLabel>E-mail</S.EditContainerLabel>
          <S.EditContainerValue>{email || ''}</S.EditContainerValue>
        </S.EditContainerBlock>
        <S.EditContainerEditButton
          disabled={isDisabled}
          onClick={() => {
            if (!isDisabled) {
              setIsEditingEmail(true);
            }
          }}
        >
          Edit
        </S.EditContainerEditButton>
      </S.EditContainer>
      {provider === 'email' ? (
        <S.EditContainer marginBottom="24px">
          <S.EditContainerBlock>
            <S.EditContainerLabel>Password</S.EditContainerLabel>
            <S.EditContainerValue>••••••••••</S.EditContainerValue>
          </S.EditContainerBlock>
          <S.EditContainerEditButton
            onClick={() => {
              setIsEditingPassword(true);
            }}
          >
            Edit
          </S.EditContainerEditButton>
        </S.EditContainer>
      ) : (
        <S.EditContainer marginBottom="24px" style={{ alignItems: 'flex-end' }}>
          <S.EditContainerBlock>
            <S.EditContainerLabel>Linked Account</S.EditContainerLabel>
            <S.EditContainerValue>
              One-click sign in only (not shown on profile)
            </S.EditContainerValue>
          </S.EditContainerBlock>
          <S.EditContainerEditButton disabled={isDisabled}>
            <AccountSettingsGoogleIcon />
          </S.EditContainerEditButton>
        </S.EditContainer>
      )}
      <Tooltip
        title={
          isShowHideDisabled
            ? 'To unlock this setting, fill in all required fields on the Work Preferences page.'
            : ''
        }
        arrow
        placement="top"
      >
        <S.EditContainer marginBottom="0">
          <S.EditContainerBlock>
            <S.EditContainerLabel>
              {showProfile ? 'Hide Account' : 'Show Account'}
            </S.EditContainerLabel>
            <S.EditContainerValue>
              {showProfile
                ? 'Your profile will not be available for viewing to all users and will not appear in the search results.'
                : 'Your profile will be available for viewing to all users and will appear in the search results.'}
            </S.EditContainerValue>
          </S.EditContainerBlock>
          {showProfile ? (
            <Button
              variant="outlined"
              text="Hide"
              className="hide-button"
              onClick={onHandleClickHideButton}
              disabled={isShowHideDisabled}
            />
          ) : (
            <Button
              variant="outlined"
              text="Show"
              className="hide-button"
              onClick={onHandleClickShowButton}
              disabled={isShowHideDisabled}
            />
          )}
        </S.EditContainer>
      </Tooltip>
      <S.Separator />
      <S.EditContainer marginBottom="0">
        <S.EditContainerBlock>
          <S.EditContainerLabel>Delete Account</S.EditContainerLabel>
          <S.EditContainerValue>Permanently delete your account</S.EditContainerValue>
        </S.EditContainerBlock>
        <Button
          variant="contained"
          text="Delete"
          className="red-button"
          onClick={onHandleClickDeleteButton}
        />
      </S.EditContainer>
    </>
  );
};

const ChangeEmailFormFields: FC<{
  isLoading: boolean;
  error: { message: string };
  status: number;
  toggleEditing: (
    field: keyof {
      email: boolean;
      password: boolean;
    },
    value: boolean
  ) => void;
}> = ({ isLoading, error, status, toggleEditing }) => {
  const {
    clearErrors,
    setError,
    formState: { errors },
  } = useFormContext<{ email: string }>();

  useEffect(() => {
    if (!error && (status === 204 || status === 200)) {
      clearErrors('email');
      toggleEditing('email', false);
      showSystemToast('success', 'Email Updated', 'We’ve sent verification link to your email');
    } else if (error) {
      setError(
        'email',
        { type: 'custom', message: error.message || 'Unknown error' },
        { shouldFocus: true }
      );
      showSystemToast(
        'error',
        'Error',
        'Something went wrong on our side. Please try again later.'
      );
    }
  }, [error, status, setError]);

  const isDisabled = !!errors.email || isLoading;

  return (
    <>
      <Input
        name="email"
        label="Email"
        type="email"
        rules={commonEmailValidationRules}
        parseError={parseError}
        marginbottom={32}
      />
      <Button
        variant="contained"
        type="submit"
        text="Save"
        endIcon={isLoading ? <Spinner disabled={isDisabled} variant="contained" /> : null}
        disabled={isDisabled}
      />
    </>
  );
};

const ChangePasswordFormFields: FC<{
  isLoading: boolean;
  error: { message: string };
  status: number;
}> = ({ isLoading, error, status }) => {
  const {
    setError,
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<{
    old_password: string;
    new_password: string;
    confirm_new_password: string;
  }>();
  const { send: logout } = useUserLogOut();
  const newPasswordValue = watch('new_password');
  const confirmNewPasswordValue = watch('confirm_new_password');

  useEffect(() => {
    if (!error && (status === 204 || status === 200)) {
      clearErrors('old_password');
      logout();
      showSystemToast(
        'success',
        'Password Changed',
        'Please Log In with a new password to continue'
      );
    } else if (error) {
      setError(
        'old_password',
        { type: 'custom', message: error.message || 'Unknown error' },
        { shouldFocus: true }
      );
      showSystemToast(
        'error',
        'Error',
        'Something went wrong on our side. Please try again later.'
      );
    }
  }, [error, status, setError]);

  useEffect(() => {
    if (newPasswordValue || confirmNewPasswordValue) {
      trigger('new_password').then();
      trigger('confirm_new_password').then();

      if (newPasswordValue === confirmNewPasswordValue) {
        clearErrors(['new_password', 'confirm_new_password']);
      }
    }
  }, [newPasswordValue, confirmNewPasswordValue, trigger, clearErrors]);

  const isDisabled =
    !!errors.old_password || !!errors.new_password || !!errors.confirm_new_password || isLoading;

  return (
    <>
      <Input
        label="Old Password"
        name="old_password"
        type="password"
        rules={getCommonPasswordValidationRules('Password')}
        parseError={parseError}
      />
      <Input
        label="New Password"
        name="new_password"
        type="password"
        rules={{
          ...getCommonPasswordValidationRules('Password'),
          validate: (value: string) => {
            return value === confirmNewPasswordValue || 'Passwords do not match';
          },
        }}
        parseError={parseError}
      />
      <Input
        label="Confirm New Password"
        name="confirm_new_password"
        type="password"
        rules={{
          ...getCommonPasswordValidationRules('Password'),
          validate: (value: string) => {
            return value === newPasswordValue || 'Passwords do not match';
          },
        }}
        parseError={parseError}
      />
      <Button
        variant="contained"
        type="submit"
        text="Save"
        endIcon={isLoading ? <Spinner disabled={isDisabled} variant="contained" /> : null}
        disabled={isDisabled}
      />
    </>
  );
};

const EditPasswordComponent = ({ toggleEditing }) => {
  const { isLoading: isUpdatePasswordLoading, updatePassword, error, status } = useUpdatePassword();

  const onPasswordSubmit = (formData: {
    old_password: string;
    new_password: string;
    confirm_new_password: string;
  }) => {
    updatePassword({ password: formData.new_password, oldPassword: formData.old_password });
  };
  return (
    <>
      <EditingHeader
        title="Change Password"
        onClick={() => {
          return toggleEditing('password', false);
        }}
      />
      <FormContainer
        defaultValues={{ old_password: '', new_password: '', confirm_new_password: '' }}
        onSuccess={onPasswordSubmit}
      >
        <ChangePasswordFormFields
          isLoading={isUpdatePasswordLoading}
          error={error}
          status={status}
        />
      </FormContainer>
    </>
  );
};

const EditEmailComponent = ({ toggleEditing, email }) => {
  const { isLoading, updateEmail, error, status } = useUpdateEmail();

  const onEmailSubmit = (formData: { email: string }) => {
    updateEmail(formData);
  };

  return (
    <>
      <EditingHeader
        title="Change E-mail"
        onClick={() => {
          return toggleEditing('email', false);
        }}
      />
      <FormContainer defaultValues={{ email: email || '' }} onSuccess={onEmailSubmit}>
        <ChangeEmailFormFields
          isLoading={isLoading}
          error={error}
          status={status}
          toggleEditing={toggleEditing}
        />
        {/* Getting from the request */}
      </FormContainer>
    </>
  );
};

const AccountSettings = () => {
  const {
    email,
    provider,
    privacySettings: { showProfile },
    jobSeekerProfile,
    role: { name: roleName },
  } = useCS(GlobalConfigContext, globalConfigSelectors.userData);

  const [editingState, setEditingState] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });

  const toggleEditing = (
    field: keyof {
      email: boolean;
      password: boolean;
    },
    value: boolean
  ) => {
    setEditingState((prev) => {
      return { ...prev, [field]: value };
    });
  };

  const outletComponent = useMemo(() => {
    if (editingState.email)
      return <EditEmailComponent toggleEditing={toggleEditing} email={email} />;
    if (editingState.password) return <EditPasswordComponent toggleEditing={toggleEditing} />;

    return (
      <AccountSettingsMainContent
        email={email}
        provider={provider}
        showProfile={showProfile}
        jobSeekerProfile={jobSeekerProfile}
        roleName={roleName}
        setIsEditingEmail={(value) => {
          return toggleEditing('email', value);
        }}
        setIsEditingPassword={(value) => {
          return toggleEditing('password', value);
        }}
      />
    );
  }, [editingState, email, provider, showProfile]);

  return <CS.ProfileMainWrapper>{outletComponent}</CS.ProfileMainWrapper>;
};

export default AccountSettings;
